<script setup lang="ts">
import { ref, computed, watch, onBeforeMount, nextTick } from "vue";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import { v4 as uuidv4 } from "uuid";
import { useI18n } from "vue-i18n";
import router from "@/router/router";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
import { required } from "@/ui/validation/validationRules";
import { useApiClient } from "@/api/client";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { useRuleModelVersion } from "@/modules/rule_engine/composables/useRuleModelVersion";
import { useRuleHistoryShortcuts } from "@/modules/rule_engine/composables/useRuleHistoryShortcuts";
import { useRuleEngineAiSummaryChannel } from "@/modules/rule_engine/composables/useRuleEngineAiSummaryChannel";
import {
	parseAiSummary,
	RULE_ENGINE_AI_SUMMARY_PERMISSION,
} from "@/modules/rule_engine/aiSummaryFormat";
import { useBaseStore } from "@/stores/base";
import {
	Rule,
	RuleSetRule,
	createRuleSet,
	createRuleSetRule,
} from "@/modules/rule_engine/ruleEngineClasses";
import RuleList from "@/modules/rule_engine/components/RuleList.vue";
import GroupTree from "@/modules/rule_engine/components/GroupTree.vue";
import ConfigureConditions from "@/modules/rule_engine/components/conditions/ConfigureConditions.vue";
import DisplayConditions from "@/modules/rule_engine/components/conditions/DisplayConditions.vue";
import ConfigureVariables from "@/modules/rule_engine/components/ConfigureVariables.vue";
import HistoryControls from "@/modules/rule_engine/components/ui/HistoryControls.vue";
import RuleSetVersionType = App.Enums.RuleSetVersionType;

const { t } = useI18n();
const route = useRoute();
const store = useRuleEnginedStore();

const model_uuid = String(route.params.model_uuid);
const version_uuid = String(route.params.version_uuid);

/** Where the builder navigates to, so a wrapping module keeps the user inside its own routes. */
export type BuilderRouteNames = {
	builder: string;
	versions: string;
	overview: string;
};

/*
 * Mounted directly by the router for the rule engine itself, and wrapped by other modules
 * (PQA) whose models use a different rule set type, whose own page shell already renders
 * breadcrumbs, and whose users may not have access to the rule engine's routes at all.
 */
const props = withDefaults(
	defineProps<{
		defaultVersionType?: RuleSetVersionType;
		showBreadcrumbs?: boolean;
		routeNames?: BuilderRouteNames;
	}>(),
	{
		defaultVersionType: "per_hour",
		showBreadcrumbs: true,
		routeNames: () => ({
			builder: "pricingEngine.builder",
			versions: "pricingEngine.versions",
			overview: "pricingEngine.index",
		}),
	}
);

const { isInitialized, initialize, saveAsVersion } = useRuleModelVersion(
	model_uuid,
	version_uuid,
	props.defaultVersionType
);

useRuleEngineAiSummaryChannel(version_uuid);

const breadcrumbs = ref([
	{ title: "Rule Based Engine", disabled: false, to: "/rule-engine" },
]);

const completeBreadCrumbs = async () => {
	const response = await useApiClient("ruleEngine").showModelMeta(model_uuid);

	breadcrumbs.value.push({
		title: response.isOk() ? response.value.name : model_uuid,
		disabled: false,
		to: `/rule-engine/versions/${model_uuid}`,
	});
	breadcrumbs.value.push({
		title: store.activeRuleModelVersion?.name ?? version_uuid,
		disabled: true,
		to: `/rule-engine/versions/${model_uuid}/${version_uuid}`,
	});
};

onBeforeRouteLeave((to, from, next) => {
	const leavingBuilder = !to.name
		?.toString()
		.startsWith(props.routeNames.builder);

	unsavedChangesDialog(
		"version of the model",
		leavingBuilder && store.parsedModelHasChanges,
		next
	);
});

onBeforeMount(async () => {
	await initialize();
	completeBreadCrumbs();
});

/* ---------------------------------------------------------------- navigation */

const displayed_ruleset_uuid = computed(() =>
	route.params.displayed_ruleset_uuid
		? String(route.params.displayed_ruleset_uuid)
		: null
);

const displayedRuleSetRule = computed(() => store.displayedRuleSetRule);

const isModelRoot = computed(
	() =>
		!store.ruleEngineState.displayedGroupUuid ||
		store.ruleEngineState.displayedGroupUuid === store.parsedModelType?.uuid
);

const groupAiSummary = computed(() =>
	store.ruleSetAiSummary(displayedRuleSetRule.value?.rule_set?.uuid)
);

const hasAiSummaryPermission = computed(() =>
	useBaseStore().HAS_PERMISSION(RULE_ENGINE_AI_SUMMARY_PERMISSION)
);

const versionAiSummary = computed(() =>
	parseAiSummary(store.activeRuleModelVersion?.ai_summary ?? "")
);

const groupAiSummaryParsed = computed(() =>
	parseAiSummary(groupAiSummary.value?.ai_summary ?? "")
);

const groupHasUnsavedChanges = computed(() =>
	store.ruleSetHasUnsavedChanges(displayedRuleSetRule.value?.rule_set?.uuid)
);

const showRuleSet = (ruleset_uuid?: string) => {
	store.ruleEngineState.displayedGroupUuid = ruleset_uuid ?? null;
	store.ruleEngineState.selectedRules = [];

	if (ruleset_uuid && ruleset_uuid !== displayed_ruleset_uuid.value) {
		router.push({
			name: props.routeNames.builder,
			params: {
				model_uuid,
				version_uuid,
				displayed_ruleset_uuid: ruleset_uuid,
			},
		});
	}
};

watch(displayed_ruleset_uuid, (uuid) => showRuleSet(uuid ?? undefined));

// Switching version type shows a different tree, so return to its root.
watch(
	() => store.ruleEngineState.selectedModelVersionType,
	() => showRuleSet()
);

/* ------------------------------------------------------------ selection ops */

const isConfigureVariablesVisible = ref<boolean>(false);
const isEditVersionNameVisible = ref<boolean>(false);
const isEditRuleSetNameVisible = ref<boolean>(false);
const isNamingNewGroup = ref<boolean>(false);
const newGroupName = ref<string>("");
const isTreePaneExpanded = ref<boolean>(false);

const selectedRules = computed(() => store.ruleEngineState.selectedRules);
const hasSelection = computed(() => selectedRules.value.length > 0);

const clearSelection = () => {
	store.ruleEngineState.selectedRules = [];
	isNamingNewGroup.value = false;
};

const removeRuleFrom = (ruleSetRule: RuleSetRule, uuid: string) => {
	const index = ruleSetRule.rule_set.rules.findIndex((r) => r.uuid === uuid);
	if (index !== -1) ruleSetRule.rule_set.rules.splice(index, 1);
};

/** Detaches rules from wherever they currently live, preserving order. */
const detachRules = (uuids: string[]): Rule[] => {
	const detached: Rule[] = [];

	uuids.forEach((uuid) => {
		const rule = store.findRuleByUuid(
			store.parsedModelType.rule_set.rules,
			uuid
		);
		if (!rule) return;

		detached.push(rule);
		removeRuleFrom(store.displayedRuleSetRule, uuid);
	});

	return detached;
};

const startNamingGroup = () => {
	newGroupName.value = "New group";
	isNamingNewGroup.value = true;
};

const confirmGroupSelected = () => {
	const name = newGroupName.value.trim();
	if (!name) return;

	const ruleGroup = createRuleSet(uuidv4(), name);
	ruleGroup.rules.push(...detachRules([...selectedRules.value]));

	store.displayedRuleSetRule.rule_set.rules.push(
		createRuleSetRule(uuidv4(), ruleGroup)
	);

	clearSelection();
};

const moveRulesToTarget = (uuids: string[], targetUuid: string) => {
	const parsedModel = store.parsedModelType;

	const target =
		targetUuid === parsedModel.uuid
			? parsedModel
			: store.findRuleByUuid(parsedModel.rule_set?.rules, targetUuid);

	if (!target) return;

	// Moving a group into itself or its own descendant would detach the subtree.
	const isInvalidTarget = uuids.some(
		(uuid) => uuid === targetUuid || containsGroup(uuid, targetUuid)
	);
	if (isInvalidTarget) return;

	target.rule_set.rules.push(...detachRules(uuids));
	clearSelection();
};

/** True when `ancestorUuid` is, or contains, `descendantUuid`. */
const containsGroup = (
	ancestorUuid: string,
	descendantUuid: string
): boolean => {
	const ancestor = store.findRuleByUuid(
		store.parsedModelType.rule_set.rules,
		ancestorUuid
	);

	if (!ancestor || ancestor.type !== "rule_set") return false;

	return !!store.findRuleByUuid(ancestor.rule_set.rules, descendantUuid);
};

/** No confirmation: nothing is persisted until save, and undo restores it. */
const deleteSelected = () => {
	detachRules([...selectedRules.value]);
	clearSelection();
};

/* ------------------------------------------------------ group conditions */

const isEditingGroupConditions = ref<boolean>(false);

const groupConditions = computed({
	get: () => displayedRuleSetRule.value.conditions ?? [],
	set: (conditions) => {
		displayedRuleSetRule.value.conditions = conditions;
	},
});

const editGroupConditions = async () => {
	if (!displayedRuleSetRule.value.conditions) {
		displayedRuleSetRule.value.conditions = [];
	}
	isEditingGroupConditions.value = true;
	await nextTick();
};

/* ---------------------------------------------------------------------- save */

/**
 * A version that is or has been live cannot be edited in place, so saving it
 * always creates a new version. That used to happen silently; now the button
 * says so and the alternative is simply not offered.
 */
const isLiveVersion = computed(
	() =>
		!!store.activeRuleModelVersion?.is_active ||
		!!store.activeRuleModelVersion?.was_active
);

const versionName = computed(
	() => store.activeRuleModelVersion?.name || "this version"
);

const persist = async (saveStrategy: "new" | "replace") => {
	const response = await saveAsVersion(saveStrategy);

	if (response.isOk()) {
		router.push({
			name: props.routeNames.versions,
			params: {
				model_uuid: response.value.rule_model_uuid,
				version_uuid: response.value.uuid,
			},
		});
	}
};

const backToOverview = () => router.push({ name: props.routeNames.overview });

useRuleHistoryShortcuts();
</script>

<template>
	<configure-variables
		v-if="isConfigureVariablesVisible"
		v-model="isConfigureVariablesVisible"
	/>

	<main-breadcrumbs v-if="showBreadcrumbs" :breadcrumbs="breadcrumbs" />

	<v-fade-transition hide-on-leave>
		<div v-if="isInitialized && displayedRuleSetRule">
			<v-card border class="mt-10" :elevation="1">
				<!-- header -->
				<v-sheet color="grey-lighten-4">
					<div class="d-flex align-center pa-3">
						<v-card-title class="d-flex py-0">
							<v-menu
								v-model="isEditVersionNameVisible"
								:close-on-content-click="false"
								location="bottom"
							>
								<template v-slot:activator="{ props }">
									<div class="hover-underline" v-bind="props">
										{{ store.activeRuleModelVersion.name || "Unnamed Version" }}
										<v-icon
											icon="fa:fa-solid fa-pen edit-icon"
											size="12"
											class="ml-1"
										/>
									</div>
								</template>
								<v-text-field
									hide-details
									variant="solo"
									v-model="store.activeRuleModelVersion.name"
									:rules="[required(t)]"
									placeholder="Enter version name"
									autofocus
								/>
							</v-menu>
						</v-card-title>
						<v-spacer />

						<span v-if="store.parsedModelHasChanges" class="dirty-badge mr-3">
							<v-icon size="8" class="mr-1">mdi-circle</v-icon>unsaved changes
						</span>

						<history-controls class="mr-3" />

						<v-btn
							icon="mdi-cog"
							color="primary"
							variant="tonal"
							density="comfortable"
							v-tooltip="'Configure variables'"
							@click="isConfigureVariablesVisible = true"
						/>
					</div>

					<div v-if="hasAiSummaryPermission" class="px-3 pb-3">
						<!-- Always the same collapsible container, regardless of state,
						     so switching between dirty/generating/failed/completed never
						     changes the collapsed header's height and jumps the page. -->
						<v-expansion-panels class="ai-summary">
							<v-expansion-panel elevation="0" class="rounded-lg bg-grey-lighten-3">
								<v-expansion-panel-title>AI summary</v-expansion-panel-title>
								<v-expansion-panel-text>
									<div
										v-if="store.parsedModelHasChanges"
										class="text-medium-emphasis"
									>
										AI summary will be available after saving the model.
									</div>
									<div
										v-else-if="store.activeRuleModelVersion?.ai_summary_status === 'failed'"
										class="text-medium-emphasis"
									>
										Summary unavailable.
									</div>
									<div
										v-else-if="
											store.activeRuleModelVersion?.ai_summary_status !==
											'completed'
										"
										class="d-flex align-center text-medium-emphasis"
									>
										<v-progress-circular
											indeterminate
											size="16"
											width="2"
											class="mr-2"
										/>
										Generating summary...
									</div>
									<div v-else class="ai-summary__text">
										<p v-if="versionAiSummary.lead" class="ai-summary__lead">
											{{ versionAiSummary.lead }}
										</p>
										<ul
											v-if="versionAiSummary.bullets.length"
											class="ai-summary__list"
										>
											<li v-for="(bullet, i) in versionAiSummary.bullets" :key="i">
												{{ bullet }}
											</li>
										</ul>
									</div>
								</v-expansion-panel-text>
							</v-expansion-panel>
						</v-expansion-panels>
					</div>

					<v-sheet class="w-100 px-3 d-flex" color="secondary">
						<v-card-subtitle class="mt-3">Rules processed:</v-card-subtitle>
						<v-tabs
							density="compact"
							class="w-100"
							v-model="store.ruleEngineState.selectedModelVersionType"
							align-tabs="start"
							height="40"
						>
							<v-tab
								:prepend-icon="
									t(`rule_engine.versionTypes.${versionType}.icon`)
								"
								v-for="versionType in store.modelVersionTypes"
								:key="versionType"
								:value="versionType"
								>{{ t(`rule_engine.versionTypes.${versionType}.label`) }}</v-tab
							>
						</v-tabs>
					</v-sheet>
				</v-sheet>

				<!-- tree + detail -->
				<div
					class="builder-body"
					:class="{ 'builder-body--tree-collapsed': !isTreePaneExpanded }"
				>
					<aside class="tree-pane">
						<div class="tree-heading">
							<v-btn
								:icon="
									isTreePaneExpanded ? 'mdi-chevron-left' : 'mdi-chevron-right'
								"
								variant="text"
								size="x-small"
								density="comfortable"
								:aria-expanded="isTreePaneExpanded"
								aria-controls="rule-engine-structure"
								v-tooltip="
									isTreePaneExpanded ? 'Collapse structure' : 'Expand structure'
								"
								@click="isTreePaneExpanded = !isTreePaneExpanded"
							/>
							<span class="tree-heading__label">Structure</span>
						</div>
						<span class="tree-rail-label" aria-hidden="true">Structure</span>
						<div
							id="rule-engine-structure"
							class="tree-content"
							:inert="!isTreePaneExpanded"
						>
							<group-tree
								v-if="store.parsedModelType"
								:node="store.parsedModelType"
								:selected-uuid="store.ruleEngineState.displayedGroupUuid"
								is-root
								@select="showRuleSet"
								@drop-rules="
									({ uuids, targetUuid }) =>
										moveRulesToTarget(uuids, targetUuid)
								"
							/>
							<div class="tree-hint">Drag rules onto a group to move them.</div>
						</div>
					</aside>

					<section class="detail-pane">
						<div class="d-flex align-center mb-4">
							<v-menu
								v-model="isEditRuleSetNameVisible"
								:close-on-content-click="false"
								location="bottom"
							>
								<template v-slot:activator="{ props }">
									<h3 class="group-title hover-underline" v-bind="props">
										{{
											displayedRuleSetRule.rule_set.name || "Unnamed Ruleset"
										}}
										<v-icon
											icon="fa:fa-solid fa-pen"
											size="12"
											class="ml-1 edit-icon"
										/>
									</h3>
								</template>
								<v-text-field
									variant="solo"
									width="220"
									v-model="displayedRuleSetRule.rule_set.name"
									:rules="[required(t)]"
									placeholder="Enter ruleset name"
									autofocus
								/>
							</v-menu>
						</div>

						<!-- group AI summary — shown for the root of a tab too, since a
						     root rule set can be its own distinct sub-model (e.g. a
						     per-day vs per-transaction split) worth summarising on its own.
						     Always the same collapsible container, regardless of state, so
						     switching between dirty/generating/failed/completed never
						     changes the collapsed header's height and jumps the page. -->
						<div v-if="hasAiSummaryPermission" class="mb-4">
							<v-expansion-panels class="ai-summary">
								<v-expansion-panel elevation="0" class="rounded-lg bg-grey-lighten-3">
									<v-expansion-panel-title>AI summary</v-expansion-panel-title>
									<v-expansion-panel-text>
										<div
											v-if="groupHasUnsavedChanges"
											class="text-medium-emphasis"
										>
											AI summary will be available after saving the model.
										</div>
										<div
											v-else-if="groupAiSummary?.ai_summary_status === 'failed'"
											class="text-medium-emphasis"
										>
											Summary unavailable.
										</div>
										<div
											v-else-if="
												groupAiSummary?.ai_summary_status !== 'completed'
											"
											class="d-flex align-center text-medium-emphasis"
										>
											<v-progress-circular
												indeterminate
												size="16"
												width="2"
												class="mr-2"
											/>
											Generating summary...
										</div>
										<div v-else class="ai-summary__text">
											<p v-if="groupAiSummaryParsed.lead" class="ai-summary__lead">
												{{ groupAiSummaryParsed.lead }}
											</p>
											<ul
												v-if="groupAiSummaryParsed.bullets.length"
												class="ai-summary__list"
											>
												<li v-for="(bullet, i) in groupAiSummaryParsed.bullets" :key="i">
													{{ bullet }}
												</li>
											</ul>
										</div>
									</v-expansion-panel-text>
								</v-expansion-panel>
							</v-expansion-panels>
						</div>

						<!-- group conditions, inline -->
						<div v-if="!isModelRoot" class="mb-5">
							<template v-if="isEditingGroupConditions">
								<configure-conditions v-model="groupConditions" />
								<div class="d-flex mt-2">
									<v-spacer />
									<v-btn
										variant="text"
										color="primary"
										@click="isEditingGroupConditions = false"
									>
										Done
									</v-btn>
								</div>
							</template>
							<template v-else>
								<v-card-title class="px-0"
									>This group applies when</v-card-title
								>
								<v-card class="pa-4" variant="tonal">
									<display-conditions
										v-if="displayedRuleSetRule.conditions?.length > 0"
										:conditions="displayedRuleSetRule.conditions"
									/>
									<i v-else>Always — no conditions on this group.</i>
									<div class="mt-2">
										<v-btn
											variant="text"
											color="primary"
											prepend-icon="mdi-pencil"
											@click="editGroupConditions"
										>
											{{
												displayedRuleSetRule.conditions?.length > 0
													? "Edit conditions"
													: "Add conditions"
											}}
										</v-btn>
									</div>
								</v-card>
							</template>
						</div>

						<rule-list
							v-model="displayedRuleSetRule.rule_set.rules"
							selectable
							@navigate-to-group="(rule) => showRuleSet(rule.uuid)"
						/>
					</section>
				</div>
			</v-card>

			<div class="pt-8 d-flex align-center">
				<v-spacer />

				<span
					v-if="isLiveVersion && store.parsedModelHasChanges"
					class="save-note mr-4"
				>
					“{{ versionName }}” is live — saving creates a new version
				</span>

				<v-btn color="error" variant="text" @click="backToOverview">
					Close editor
				</v-btn>

				<!-- Live versions can only be saved one way, so no split button. -->
				<v-btn
					v-if="isLiveVersion"
					class="ml-5"
					color="primary"
					variant="flat"
					:disabled="!store.parsedModelHasChanges"
					@click="persist('new')"
				>
					Save as new version
				</v-btn>

				<v-btn-group v-else class="ml-5" divided color="primary" variant="flat">
					<v-btn
						:disabled="!store.parsedModelHasChanges"
						@click="persist('replace')"
					>
						Save draft
					</v-btn>
					<v-menu location="top end">
						<template v-slot:activator="{ props }">
							<v-btn
								v-bind="props"
								:disabled="!store.parsedModelHasChanges"
								icon="mdi-menu-down"
								v-tooltip="'Other save options'"
							/>
						</template>
						<v-list density="compact" width="320">
							<v-list-item
								title="Save draft"
								:subtitle="`Updates “${versionName}” in place`"
								@click="persist('replace')"
							/>
							<v-list-item
								title="Save as new version"
								:subtitle="`Keeps “${versionName}”, creates a new version`"
								@click="persist('new')"
							/>
						</v-list>
					</v-menu>
				</v-btn-group>
			</div>

			<!-- contextual selection bar -->
			<v-slide-y-reverse-transition>
				<div v-if="hasSelection" class="action-bar">
					<span class="mr-4">{{ selectedRules.length }} selected</span>

					<template v-if="isNamingNewGroup">
						<v-text-field
							v-model="newGroupName"
							density="compact"
							variant="solo"
							hide-details
							autofocus
							style="max-width: 240px"
							@keyup.enter="confirmGroupSelected"
						/>
						<v-btn
							class="ml-2"
							variant="tonal"
							color="primary"
							@click="confirmGroupSelected"
						>
							Create group
						</v-btn>
						<v-btn
							variant="text"
							class="ml-1"
							@click="isNamingNewGroup = false"
						>
							Cancel
						</v-btn>
					</template>
					<template v-else>
						<v-btn variant="tonal" color="primary" @click="startNamingGroup">
							Group them
						</v-btn>
						<v-btn
							variant="text"
							color="error"
							class="ml-2"
							@click="deleteSelected"
						>
							Delete
						</v-btn>
						<span class="ml-4 hint">or drag them onto a group</span>
					</template>

					<v-spacer />
					<v-btn
						icon
						variant="text"
						density="comfortable"
						@click="clearSelection"
					>
						<v-icon size="18">mdi-close</v-icon>
					</v-btn>
				</div>
			</v-slide-y-reverse-transition>
		</div>
		<v-progress-linear
			indeterminate
			class="mt-10"
			color="primary"
			height="5"
			v-else
		/>
	</v-fade-transition>
</template>

<style lang="scss" scoped>
$tree-slide: 260ms cubic-bezier(0.4, 0, 0.2, 1);
/* Content fades out before the column closes, and back in once it has opened. */
$tree-fade-out: opacity 120ms ease 0s;
$tree-fade-in: opacity 160ms ease 120ms;

.builder-body {
	display: grid;
	grid-template-columns: minmax(190px, 260px) 1fr;
	/*
	 * "stretch" (the grid default) so .tree-pane's box spans the full row
	 * height and its border-right divider runs edge-to-edge, even though
	 * its own content is shorter than .detail-pane's. This doesn't affect
	 * the tree pane's sticky behavior: sticky pins the box's top edge to
	 * the viewport, and the content stays anchored at the top of the box
	 * either way — stretching only extends the empty space (and border)
	 * below it.
	 */
	align-items: stretch;
	transition: grid-template-columns $tree-slide;
}

.builder-body--tree-collapsed {
	grid-template-columns: 48px 1fr;
}

.tree-pane {
	border-right: 1px solid rgba(0, 0, 0, 0.08);
	padding: 1rem 0.5rem;
	position: sticky;
	top: 0;
	min-height: 320px;
	/* Clips the tree while the column animates shut. */
	overflow: hidden;
}

.tree-heading {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	font-size: 0.7rem;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	opacity: 0.55;
	padding: 0 0 0.5rem;
}

.tree-heading__label {
	white-space: nowrap;
}

/*
 * Holding the expanded width means the tree slides out behind the clip rather
 * than reflowing itself into a 48px ribbon on the way.
 */
.tree-content {
	min-width: 170px;
}

.tree-content,
.tree-heading__label {
	opacity: 1;
	transition: $tree-fade-in;
}

.builder-body--tree-collapsed .tree-content,
.builder-body--tree-collapsed .tree-heading__label {
	opacity: 0;
	transition: $tree-fade-out;
}

/* Vertical caption for the collapsed rail, cross-faded with the heading above. */
.tree-rail-label {
	position: absolute;
	top: 3rem;
	left: 50%;
	transform: translateX(-50%);
	writing-mode: vertical-rl;
	font-size: 0.7rem;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	pointer-events: none;
	opacity: 0;
	transition: $tree-fade-out;
}

.builder-body--tree-collapsed .tree-rail-label {
	opacity: 0.55;
	transition: $tree-fade-in;
}

@media (prefers-reduced-motion: reduce) {
	.builder-body,
	.tree-content,
	.tree-heading__label,
	.tree-rail-label,
	.builder-body--tree-collapsed .tree-content,
	.builder-body--tree-collapsed .tree-heading__label,
	.builder-body--tree-collapsed .tree-rail-label {
		transition: none;
	}
}

.tree-hint {
	font-size: 0.7rem;
	opacity: 0.45;
	padding: 1rem 0.5rem 0;
	line-height: 1.4;
}

.detail-pane {
	padding: 1rem 1.25rem;
	min-width: 0;
}

.group-title {
	font-size: 1.05rem;
	font-weight: 600;
	cursor: pointer;
}

.ai-summary__text {
	overflow-wrap: anywhere;
	line-height: 1.5;
	font-size: 0.9rem;
}

.ai-summary__lead {
	margin: 0 0 0.6rem;
}

.ai-summary__list {
	margin: 0;
	padding-left: 1.2rem;

	li {
		margin-bottom: 0.4rem;

		&::marker {
			color: rgb(var(--v-theme-primary));
		}

		&:last-child {
			margin-bottom: 0;
		}
	}
}

.hover-underline:hover {
	cursor: pointer;
	text-decoration: underline;
}

.edit-icon {
	opacity: 0.8;
}

.action-bar {
	position: sticky;
	bottom: 1rem;
	display: flex;
	align-items: center;
	margin-top: 1rem;
	padding: 0.6rem 1rem;
	border-radius: 8px;
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
	border: 1px solid rgba(0, 0, 0, 0.08);
	z-index: 5;
}

.hint {
	font-size: 0.8rem;
	opacity: 0.6;
}

.save-note {
	font-size: 0.8rem;
	opacity: 0.7;
}

.dirty-badge {
	display: inline-flex;
	align-items: center;
	font-size: 0.75rem;
	opacity: 0.7;
	white-space: nowrap;
}
</style>
